import type {
  LineStatus,
  Phase,
  Priority,
  ProductSummary,
  ProjectCategory,
  ProjectLifecycle,
  StageStatus,
} from '../shared/types';

const PHASES: Phase[] = ['PD1', 'PD2', 'PD3', 'PD4', 'PD5'];

export const PRIORITY_LABELS: Record<Priority, string> = {
  alta: 'Alta',
  media: 'Média',
  baixa: 'Baixa',
};
/** Ordem de prioridade (menor = mais prioritário) para ordenar o painel. */
export const PRIORITY_ORDER: Record<Priority, number> = { alta: 0, media: 1, baixa: 2 };

export const CATEGORY_LABELS: Record<ProjectCategory, string> = {
  inovacao: 'Inovação / Novo produto',
  extensao_linha: 'Extensão de linha',
  melhoria: 'Melhoria de produto',
  reducao_custo: 'Redução de custo',
  regulatorio: 'Regulatório / Obrigatório',
  longo_prazo: 'Longo prazo / Roadmap',
};

/**
 * Categorias oferecidas ao criar/filtrar projetos. As demais (redução de custo,
 * regulatório, longo prazo) foram descontinuadas — os rótulos acima ficam apenas
 * para exibir dados antigos que ainda as usem.
 */
export const SELECTABLE_CATEGORIES: ProjectCategory[] = ['inovacao', 'extensao_linha', 'melhoria'];

export function PriorityBadge({ priority }: { priority: Priority }) {
  return <span className={`prio prio-${priority}`}>{PRIORITY_LABELS[priority]}</span>;
}

export function CategoryTag({ category }: { category: ProjectCategory }) {
  return <span className="cat-tag">{CATEGORY_LABELS[category]}</span>;
}

export const LINE_STATUS_LABEL: Record<LineStatus, string> = {
  sem_data: 'Sem data',
  no_prazo: 'No prazo',
  atencao: 'Atenção',
  atrasado: 'Atrasado',
  concluido: 'Concluído',
  pausado: 'Em espera',
  cancelado: 'Cancelado',
};

export const LIFECYCLE_LABELS: Record<ProjectLifecycle, string> = {
  ativo: 'Ativo',
  pausado: 'Em espera',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
};

export const STAGE_STATUS_LABEL: Record<StageStatus, string> = {
  nao_iniciado: 'Não iniciado',
  em_andamento: 'Em andamento',
  concluido: 'Concluído',
};

export function StatusBadge({ status }: { status: LineStatus }) {
  return <span className={`badge ${status}`}>{LINE_STATUS_LABEL[status]}</span>;
}

export function PhaseBand({ summary }: { summary: ProductSummary }) {
  return (
    <div className="phaseband" title="PD1 · PD2 · PD3 · PD4 · PD5">
      {PHASES.map((ph) => (
        <span key={ph} className={`seg ${summary.phaseStatus[ph]}`} />
      ))}
    </div>
  );
}

export function Progress({ value }: { value: number }) {
  return (
    <span className="progress" aria-label={`${Math.round(value * 100)}% concluído`}>
      <i style={{ width: `${Math.round(value * 100)}%` }} />
    </span>
  );
}

export function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

export function desvioLabel(dias: number): string {
  if (dias > 0) return `+${dias} d`;
  if (dias < 0) return `${dias} d`;
  return '0 d';
}

/** Rótulo do desvio vs. meta de lançamento (previsão − data-alvo). */
export function metaInfo(desvioMeta: number | null): { text: string; tone: 'ok' | 'warn' | 'none' } {
  if (desvioMeta == null) return { text: 'sem meta', tone: 'none' };
  if (desvioMeta <= 0) return { text: `${-desvioMeta} d de folga`, tone: 'ok' };
  return { text: `+${desvioMeta} d vs meta`, tone: 'warn' };
}
